import { useState, useCallback, useEffect, startTransition } from 'react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Trash2 } from 'lucide-react'
import { OperatorSelect } from './operator-select'
import { DATE_OPERATORS } from './filter-types'
import type { FilterOperator } from '@/types/api'

interface DateFilterEditorProps {
  value: string | undefined
  operator: FilterOperator
  onValueChange: (value: string | undefined) => void
  onOperatorChange: (operator: FilterOperator) => void
  onRemove?: () => void
  label?: string
}

export function DateFilterEditor({
  value,
  operator,
  onValueChange,
  onOperatorChange,
  onRemove,
  label,
}: DateFilterEditorProps) {
  const [localValue, setLocalValue] = useState(() => value ?? '')

  // Sync local state when prop changes
  useEffect(() => {
    startTransition(() => {
      setLocalValue(value ?? '')
    })
  }, [value])

  const handleApply = useCallback(() => {
    onValueChange(localValue || undefined)
  }, [localValue, onValueChange])

  return (
    <div className="flex flex-col gap-3 p-3">
      <div className="flex items-center gap-2">
        <OperatorSelect
          value={operator}
          options={DATE_OPERATORS}
          onChange={onOperatorChange}
          label={label}
          className="flex-1 h-8"
        />
        {onRemove && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onRemove}
            className="h-8 w-8 shrink-0"
          >
            <Trash2 className="h-4 w-4 text-destructive/50" />
          </Button>
        )}
      </div>
      <Input
        type="date"
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        className="h-8 text-sm"
        autoFocus
      />
      <Button size="sm" onClick={handleApply} className="w-full">
        Apply
      </Button>
    </div>
  )
}
